// Parser OFX (Open Financial Exchange) — Robusto para bancos brasileiros

export interface OFXTransaction {
  id: string;           // FITID
  date: Date;           // DTPOSTED
  amount: number;       // TRNAMT (positive=credit, negative=debit)
  type: string;         // TRNTYPE
  description: string;  // Descrição completa combinada
  checkNumber?: string; // CHECKNUM
  payeeName?: string;
  payeeId?: string;
  refNumber?: string;
  memo?: string;
  rawDescription?: string;
}

export interface OFXStatement {
  bankId: string;
  accountId: string;
  accountType: string;
  startDate: Date;
  endDate: Date;
  ledgerBalance: number;
  availableBalance?: number;
  transactions: OFXTransaction[];
}

function parseOFXDate(dateStr: string): Date {
  const cleanDate = dateStr.replace(/\[.*\]/, '').trim();
  const year = parseInt(cleanDate.substring(0, 4));
  const month = parseInt(cleanDate.substring(4, 6)) - 1;
  const day = parseInt(cleanDate.substring(6, 8));
  const hour = cleanDate.length >= 10 ? parseInt(cleanDate.substring(8, 10)) : 0;
  const minute = cleanDate.length >= 12 ? parseInt(cleanDate.substring(10, 12)) : 0;
  const second = cleanDate.length >= 14 ? parseInt(cleanDate.substring(12, 14)) : 0;
  return new Date(year, month, day, hour, minute, second);
}

function extractTagValue(content: string, tagName: string): string {
  const regex = new RegExp(`<${tagName}>([^<\\n]+)`, 'i');
  const match = content.match(regex);
  return match ? match[1].trim() : '';
}

function extractPayeeName(block: string): string {
  const payeeMatch = block.match(/<PAYEE>([\s\S]*?)(<\/PAYEE>|<(?!NAME|ADDR|CITY|STATE|POSTALCODE|COUNTRY|PHONE))/i);
  if (payeeMatch) {
    return extractTagValue(payeeMatch[1], 'NAME');
  }
  return '';
}

function sanitizeAmount(raw: string): number {
  const cleaned = raw.replace(/\s+/g, '').replace(/^\+/, '').replace(',', '.');
  const val = parseFloat(cleaned);
  return isNaN(val) ? 0 : val;
}

function parseTransactionBlock(block: string, index: number): OFXTransaction | null {
  try {
    const trnType = extractTagValue(block, 'TRNTYPE');
    const dtPosted = extractTagValue(block, 'DTPOSTED');
    const trnAmt = extractTagValue(block, 'TRNAMT');
    let fitId = extractTagValue(block, 'FITID');
    const memo = extractTagValue(block, 'MEMO');
    const name = extractTagValue(block, 'NAME');
    const checkNum = extractTagValue(block, 'CHECKNUM');
    const refNum = extractTagValue(block, 'REFNUM');
    const payeeId = extractTagValue(block, 'PAYEEID');
    const payeeName = extractPayeeName(block);

    if (!trnAmt) return null;

    // FITID fallback for banks that omit it
    if (!fitId) {
      fitId = `auto_${index}_${dtPosted || Date.now()}`;
    }

    const effectivePayeeName = payeeName || name || '';
    let fullDescription = '';
    if (effectivePayeeName && memo) {
      fullDescription = `${effectivePayeeName} - ${memo}`;
    } else if (memo) {
      fullDescription = memo;
    } else if (effectivePayeeName) {
      fullDescription = effectivePayeeName;
    } else {
      fullDescription = 'Transação sem descrição';
    }

    return {
      id: fitId,
      date: dtPosted ? parseOFXDate(dtPosted) : new Date(),
      amount: sanitizeAmount(trnAmt),
      type: trnType || 'OTHER',
      description: fullDescription,
      checkNumber: checkNum || undefined,
      payeeName: effectivePayeeName || undefined,
      payeeId: payeeId || undefined,
      refNumber: refNum || undefined,
      memo: memo || undefined,
      rawDescription: memo || name || undefined,
    };
  } catch {
    return null;
  }
}

function extractTransactions(content: string): OFXTransaction[] {
  const transactions: OFXTransaction[] = [];

  // Strategy 1: Standard XML-closed tags
  const stmtTrnRegex = /<STMTTRN>([\s\S]*?)<\/STMTTRN>/gi;
  let match;
  let foundClosed = false;

  while ((match = stmtTrnRegex.exec(content)) !== null) {
    foundClosed = true;
    const tx = parseTransactionBlock(match[1], transactions.length);
    if (tx) transactions.push(tx);
  }

  // Strategy 2: SGML without closing tags (common in Brazilian banks)
  if (!foundClosed) {
    const blocks = content.split(/<STMTTRN>/i);
    for (let i = 1; i < blocks.length; i++) {
      // Block ends at next <STMTTRN>, </BANKTRANLIST>, or </OFX>
      const block = blocks[i].split(/<(?:STMTTRN|\/BANKTRANLIST|\/OFX)/i)[0];
      const tx = parseTransactionBlock(block, transactions.length);
      if (tx) transactions.push(tx);
    }
  }

  return transactions;
}

export function parseOFX(content: string): OFXStatement {
  try {
    const normalized = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    const bankId = extractTagValue(normalized, 'BANKID');
    const acctId = extractTagValue(normalized, 'ACCTID');
    const acctType = extractTagValue(normalized, 'ACCTTYPE');
    const dtStart = extractTagValue(normalized, 'DTSTART');
    const dtEnd = extractTagValue(normalized, 'DTEND');

    const ledgerBalBlock = normalized.match(/<LEDGERBAL>([\s\S]*?)<\/LEDGERBAL>/i);
    const availBalBlock = normalized.match(/<AVAILBAL>([\s\S]*?)<\/AVAILBAL>/i);

    let ledgerBalance = 0;
    let availableBalance: number | undefined;

    if (ledgerBalBlock) {
      ledgerBalance = sanitizeAmount(extractTagValue(ledgerBalBlock[1], 'BALAMT'));
    }
    if (availBalBlock) {
      const val = sanitizeAmount(extractTagValue(availBalBlock[1], 'BALAMT'));
      availableBalance = val !== 0 ? val : undefined;
    }

    const transactions = extractTransactions(normalized);

    return {
      bankId: bankId || 'UNKNOWN',
      accountId: acctId || 'UNKNOWN',
      accountType: acctType || 'CHECKING',
      startDate: dtStart ? parseOFXDate(dtStart) : new Date(),
      endDate: dtEnd ? parseOFXDate(dtEnd) : new Date(),
      ledgerBalance,
      availableBalance,
      transactions,
    };
  } catch {
    // Return empty statement on catastrophic parse failure
    return {
      bankId: 'UNKNOWN',
      accountId: 'UNKNOWN',
      accountType: 'CHECKING',
      startDate: new Date(),
      endDate: new Date(),
      ledgerBalance: 0,
      transactions: [],
    };
  }
}

export function detectBankFromOFX(statement: OFXStatement): string | null {
  const bankCodes: Record<string, string> = {
    '001': 'bb', '033': 'santander', '104': 'caixa', '237': 'bradesco',
    '341': 'itau', '399': 'hsbc', '422': 'safra', '745': 'citibank',
    '756': 'sicoob', '260': 'nubank', '077': 'inter', '336': 'c6',
    '212': 'original', '290': 'pagseguro', '380': 'picpay', '748': 'sicredi',
  };

  if (statement.bankId && bankCodes[statement.bankId]) {
    return bankCodes[statement.bankId];
  }
  for (const [code, bank] of Object.entries(bankCodes)) {
    if (statement.bankId.includes(code)) return bank;
  }
  return null;
}

export function isValidOFX(content: string): boolean {
  const hasHeader = content.includes('OFXHEADER') || content.includes('<OFX>');
  const hasTransactions = content.includes('<STMTTRN>') || content.includes('<BANKTRANLIST>');
  return hasHeader || hasTransactions;
}
